/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useRef } from 'react';
import TurnstileGate from '../components/security/TurnstileGate';
import useCaptchaConfig from '../hooks/useCaptchaConfig';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff, AlertTriangle, Clock, Mail, Shield, Smartphone } from 'lucide-react';
import api from '../services/api';

// ── Decorative radial glows (shared chrome) ─────────────────────────
const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
      }}
    />
  </>
);

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockoutInfo, setLockoutInfo] = useState(null);
  const [passwordExpiryWarning, setPasswordExpiryWarning] = useState(null);
  const [verificationNeeded, setVerificationNeeded] = useState(null);
  const [deletedAccount, setDeletedAccount] = useState(false);

  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState(null);
  // Captcha (Cloudflare Turnstile). Always required for browser logins
  // when the server-side kill switch is on. /api/public/config is fetched
  // fresh on every page load (no client cache) so flipping CAPTCHA_ENABLED
  // takes effect on the next navigation for everyone.
  const captchaCfg = useCaptchaConfig();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaStatus, setCaptchaStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  // Flash message from a prior "This wasn't me" / forced-logout redirect
  // (see api.js SESSION_REVOKED handler). Shows in the same yellow
  // warning slot as lockoutInfo, then clears so a refresh won't re-show it.
  useEffect(() => {
    try {
      const flash = sessionStorage.getItem('flashMessage');
      if (flash) {
        setLockoutInfo({ type: 'warning', message: flash });
        sessionStorage.removeItem('flashMessage');
      }
    } catch (_) { /* private mode / disabled storage — silent no-op */ }
  }, []);
  const captchaGateRef = useRef(null);
  const needsCaptcha = captchaCfg.ready && captchaCfg.captchaEnabled;
  const captchaBlocks = needsCaptcha && (!captchaToken || captchaStatus !== 'ready');

  const { login, complete2FALogin, error } = useAuth();

  // Track options
  const tracks = [
    { value: '', label: '-- Select Track (Optional) --', icon: '' },
    { value: 'WMS', label: 'Warehouse Management', icon: '📦' },
    { value: 'iSeries', label: 'IBM iSeries/AS400', icon: '💻' },
    { value: 'Civil', label: 'Civil Construction', icon: '🏗️' },
    { value: 'Stitching', label: 'Apparel & Stitching', icon: '🧵' },
    { value: 'ERPNext', label: 'ERPNext ERP System', icon: '⚙️' },
    { value: 'SkillCode', label: 'AI Software Engineering', icon: '🖥️' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLockoutInfo(null);
    setPasswordExpiryWarning(null);
    setVerificationNeeded(null);
    setDeletedAccount(false);

    try {
      if (needsCaptcha && !captchaToken) {
        setLockoutInfo({ type: 'warning', message: 'Please complete the security check.' });
        setLoading(false);
        return;
      }
      const result = await login(email, password, captchaToken);
      // Consume the token whether or not the login succeeded — Turnstile
      // tokens are single-use; the next attempt needs a fresh one.
      if (captchaToken) {
        setCaptchaToken(null);
        if (captchaGateRef.current) captchaGateRef.current.reset();
      }

      if (result.success) {
        if (result.requires2FA) {
          setRequires2FA(true);
          setTempToken(result.tempToken);
          setLoading(false);
          return;
        }

        if (result.data?.passwordExpiryWarning) {
          setPasswordExpiryWarning(result.data.passwordExpiryWarning);
        }

        if (selectedTrack) {
          try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (user.id) {
              const updateResult = await api.updateUser(user.id, { vertical: selectedTrack });
              if (updateResult.success) {
                const updatedUser = { ...user, vertical: selectedTrack };
                localStorage.setItem('user', JSON.stringify(updatedUser));
              }
            }
          } catch (err) {
            console.error('Error updating track:', err);
          }
        }

        if (result.data?.requires2FASetup) {
          navigate('/profile?setup2fa=required');
          return;
        }

        navigate('/dashboard');
      } else {
        if (result.isSuspended || result.code === 'LOGIN_BLOCKED_SUSPENDED' || result.code === 'ACCOUNT_SUSPENDED') {
          if (result.suspensionInfo) {
            localStorage.setItem('suspensionInfo', JSON.stringify(result.suspensionInfo));
          }
          navigate('/suspended');
          setLoading(false);
          return;
        }

        if (result.code === 'ACCOUNT_NOT_FOUND') {
          setDeletedAccount(true);
        } else if (result.code === 'EMAIL_NOT_VERIFIED') {
          setVerificationNeeded({
            email: result.email || email,
            message: result.error || 'Please verify your email before logging in.'
          });
        } else if (result.code === 'CAPTCHA_FAILED') {
          setLockoutInfo({ type: 'warning', message: 'Please complete the security check and try again.' });
        } else {
          // Uniform failure path (INVALID_CREDENTIALS from unknown-email OR
          // wrong-password). Widget is always mounted for browsers now,
          // so nothing to toggle here.
          setLockoutInfo({
            type: 'warning',
            message: result.error || 'Invalid credentials.'
          });
        }
      }
    } catch (err) {
      if (err.isSuspended || err.response?.data?.code === 'LOGIN_BLOCKED_SUSPENDED' || err.response?.data?.isSuspended) {
        const data = err.response?.data || {};
        if (data.suspensionInfo) {
          localStorage.setItem('suspensionInfo', JSON.stringify(data.suspensionInfo));
        }
        navigate('/suspended');
        setLoading(false);
        return;
      }

      if (err.response?.data?.code === 'ACCOUNT_NOT_FOUND') {
        setDeletedAccount(true);
      } else if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setVerificationNeeded({
          email: err.response?.data?.email || email,
          message: err.response?.data?.error || 'Please verify your email before logging in.'
        });
      } else if (err.isLockout || err.response?.data?.code === 'ACCOUNT_LOCKED') {
        const remainingMinutes = err.remainingMinutes || err.response?.data?.remainingMinutes || 30;
        setLockoutInfo({
          type: 'locked',
          message: 'Your account has been temporarily locked due to too many failed login attempts.',
          remainingMinutes
        });
      } else if (err.attemptsRemaining !== undefined) {
        setLockoutInfo({
          type: 'warning',
          message: `Invalid credentials. ${err.attemptsRemaining} attempt${err.attemptsRemaining !== 1 ? 's' : ''} remaining.`
        });
      }
    }

    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTwoFactorError(null);

    try {
      const result = await complete2FALogin(tempToken, twoFactorCode);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setTwoFactorError(result.error || 'Invalid 2FA code. Please try again.');
      }
    } catch (err) {
      setTwoFactorError(err.message || 'Failed to verify 2FA code.');
    }

    setLoading(false);
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setTempToken(null);
    setTwoFactorCode('');
    setTwoFactorError(null);
    setPassword('');
  };

  // ─── 2FA panel ─────────────────────────────────────────────────────
  if (requires2FA) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white flex items-center justify-center p-4">
        <BrandGlows />
        <div className="relative bg-white rounded-brandCard shadow-brand border border-brand-border p-8 w-full max-w-md">
          <div className="flex flex-col items-center justify-center mb-6">
            <div className="w-16 h-16 bg-brand-cyan/10 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-brand-cyan" />
            </div>
            <h2 className="text-2xl font-bold text-center text-brand-text mb-2">
              Two-Factor Authentication
            </h2>
            <p className="text-center text-sm text-brand-muted">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          {twoFactorError && (
            <div className="mb-4 p-4 rounded-brand bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{twoFactorError}</p>
            </div>
          )}

          <form onSubmit={handle2FASubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-brand-text mb-1">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Verification Code
                </div>
              </label>
              <input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
                placeholder="000000"
                maxLength={6}
                autoFocus
                required
              />
              <p className="text-xs text-brand-muted2 mt-2 text-center">
                Or enter a backup code if you don't have access to your authenticator
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || twoFactorCode.length < 6}
              className="w-full py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              <Shield className="w-4 h-4" />
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            <button
              type="button"
              onClick={cancel2FA}
              className="w-full py-2 text-brand-muted hover:text-brand-text text-sm"
            >
              Back to login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Main login panel ──────────────────────────────────────────────
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white flex items-center justify-center p-4">
      <BrandGlows />
      <div className="relative bg-white rounded-brandCard shadow-brand border border-brand-border p-8 w-full max-w-md">

        {/* ── Logo + wordmark ── */}
        <div className="flex items-center justify-center gap-2.5 mb-2">
          <img src="/logo.jpg" alt="SkillDelivery" className="h-9 w-auto" />
          <span className="font-bold text-xl tracking-tight text-brand-text">
            Skill<span className="text-brand-cyan">Delivery</span>
          </span>
        </div>
        <p className="text-center text-sm text-brand-muted mb-6">Skills, Delivered!</p>

        <h2 className="text-2xl font-bold text-center text-brand-text mb-2">Welcome Back</h2>
        <p className="text-center text-sm text-brand-muted mb-6">Sign in to continue your learning journey</p>

        {/* Account Lockout Warning */}
        {lockoutInfo && (
          <div className={`mb-4 p-4 rounded-brand flex items-start gap-3 ${
            lockoutInfo.type === 'locked'
              ? 'bg-red-50 border border-red-200'
              : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              lockoutInfo.type === 'locked' ? 'text-red-500' : 'text-yellow-500'
            }`} />
            <div>
              <p className={`text-sm font-medium ${
                lockoutInfo.type === 'locked' ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {lockoutInfo.type === 'locked' ? 'Account Locked' : 'Warning'}
              </p>
              <p className={`text-sm mt-1 ${
                lockoutInfo.type === 'locked' ? 'text-red-700' : 'text-yellow-700'
              }`}>
                {lockoutInfo.message}
              </p>
              {lockoutInfo.remainingMinutes && (
                <p className={`text-sm mt-2 flex items-center gap-1 ${
                  lockoutInfo.type === 'locked' ? 'text-red-600' : 'text-yellow-600'
                }`}>
                  <Clock className="w-4 h-4" />
                  Try again in {lockoutInfo.remainingMinutes} minute{lockoutInfo.remainingMinutes !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Password Expiry Warning */}
        {passwordExpiryWarning && (
          <div className="mb-4 p-4 rounded-brand bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Notice:</strong> {passwordExpiryWarning.message}
            </p>
            <Link
              to="/change-password"
              className="text-sm text-amber-700 hover:text-amber-900 underline mt-1 inline-block"
            >
              Change password now
            </Link>
          </div>
        )}

        {/* Deleted Account Notice */}
        {deletedAccount && (
          <div className="mb-4 p-4 rounded-brand bg-gray-50 border border-gray-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-brand-text">Account Not Found</p>
                <p className="text-sm text-brand-muted mt-1">
                  No account exists with this email address. If you previously deleted your
                  account and wish to return, please contact{' '}
                  <a
                    href="mailto:support@skilldelivery.co.in"
                    className="text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
                  >
                    support@skilldelivery.co.in
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Email Verification Needed */}
        {verificationNeeded && (
          <div className="mb-4 p-4 rounded-brand bg-blue-50 border border-blue-200">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Email Verification Required</p>
                <p className="text-sm text-blue-700 mt-1">{verificationNeeded.message}</p>
                <p className="text-xs text-blue-600 mt-2">
                  Check your inbox at <strong>{verificationNeeded.email}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
              placeholder="your@email.com"
              required
              disabled={lockoutInfo?.type === 'locked'}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
                placeholder="••••••••"
                required
                disabled={lockoutInfo?.type === 'locked'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-text"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex justify-end mt-1">
              <Link to="/forgot-password" className="text-xs text-brand-cyan hover:text-brand-cyanDark">
                Forgot password?
              </Link>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">
              Primary Track / Domain
            </label>
            <select
              value={selectedTrack}
              onChange={(e) => setSelectedTrack(e.target.value)}
              className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan bg-white"
              disabled={lockoutInfo?.type === 'locked'}
            >
              {tracks.map((track) => (
                <option key={track.value} value={track.value}>
                  {track.icon} {track.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-brand-muted2 mt-1">
              Optional - Select your primary area of expertise (defaults to WMS)
            </p>
          </div>

          {needsCaptcha && (
            <div>
              <p className="text-xs text-brand-muted mb-1">Please complete the security check:</p>
              <TurnstileGate
                ref={captchaGateRef}
                onToken={setCaptchaToken}
                onStatus={setCaptchaStatus}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading || lockoutInfo?.type === 'locked' || captchaBlocks}
            className={`w-full py-2.5 rounded-brand font-semibold flex items-center justify-center gap-2 transition-all duration-200 ${
              lockoutInfo?.type === 'locked'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0'
            }`}
          >
            <LogIn className="w-4 h-4" />
            {loading ? 'Signing in...' : lockoutInfo?.type === 'locked' ? 'Account Locked' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-sm text-brand-muted mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-brand-cyan hover:text-brand-cyanDark font-medium">
            Sign up
          </Link>
        </p>

        {error && !lockoutInfo && (
          <div className="mt-4 p-3 bg-red-50 rounded-brand text-sm text-red-700">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
